import { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import apiClient from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Search, BookOpen, GraduationCap, ChevronRight, ArrowLeft, Rocket, FolderKanban, BarChart3, FileText, Bot, ExternalLink } from 'lucide-react';

const CAT_ICONS = { getting_started: Rocket, projects: FolderKanban, analyzer: BarChart3, reports: FileText, copilot: Bot };
const GLOS_COLORS = { rentabilidad: '#2F5BEA', operativo: '#D97706', financiamiento: '#6D28D9', comercial: '#059669', cobranza: '#6D28D9', costos: '#DC2626', analisis: '#2F5BEA' };

export default function HelpPage() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [glossary, setGlossary] = useState([]);
  const [glossaryCats, setGlossaryCats] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([apiClient.get('/help/articles'), apiClient.get('/help/glossary')])
      .then(([a, g]) => { setArticles(a.data.articles); setCategories(a.data.categories); setGlossary(g.data.terms); setGlossaryCats(g.data.categories); })
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filteredArticles = useMemo(() => {
    if (!search) return articles;
    const q = search.toLowerCase();
    return articles.filter(a => a.title.toLowerCase().includes(q) || a.summary.toLowerCase().includes(q));
  }, [articles, search]);

  const filteredGlossary = useMemo(() => {
    if (!search) return glossary;
    const q = search.toLowerCase();
    return glossary.filter(t => t.term.toLowerCase().includes(q) || t.simple.toLowerCase().includes(q));
  }, [glossary, search]);

  // Article detail view
  if (selectedArticle) {
    return (
      <div className="h-full overflow-y-auto px-6 lg:px-8 py-6 max-w-[860px]" data-testid="help-article-view">
        <Button variant="ghost" size="sm" className="mb-4" onClick={() => setSelectedArticle(null)}><ArrowLeft className="h-4 w-4 mr-1.5" /> Volver</Button>
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-display text-2xl font-semibold tracking-tight mb-2">{selectedArticle.title}</h1>
          <p className="text-sm text-muted-foreground mb-6">{selectedArticle.summary}</p>
          <Card className="rounded-xl border-border/50 shadow-sm">
            <CardContent className="p-8 prose-sm" style={{ lineHeight: 1.8 }}>
              {selectedArticle.content.split('\n').map((line, i) => {
                if (line.startsWith('## ')) return <h2 key={i} className="font-display text-lg font-semibold mt-6 mb-3" style={{ color: 'var(--primary-hex)' }}>{line.replace('## ', '')}</h2>;
                if (line.startsWith('- **')) { const m = line.match(/- \*\*(.+?)\*\* — (.+)/); return m ? <div key={i} className="flex gap-2 mb-2 text-sm"><span className="font-semibold shrink-0">{m[1]}</span><span className="text-muted-foreground">— {m[2]}</span></div> : <p key={i} className="text-sm mb-1">{line}</p>; }
                if (line.startsWith('- ')) return <div key={i} className="flex gap-2 mb-1.5 text-sm"><span className="text-muted-foreground">•</span><span>{line.replace('- ', '')}</span></div>;
                if (line.startsWith('| ') && line.includes('|')) { const cells = line.split('|').filter(c => c.trim()); const isHeader = i > 0 && selectedArticle.content.split('\n')[i+1]?.startsWith('|---'); return <div key={i} className={`grid text-xs py-1.5 border-b border-border/30 ${isHeader ? 'font-semibold' : ''}`} style={{ gridTemplateColumns: `repeat(${cells.length}, 1fr)` }}>{cells.map((c, j) => <span key={j} className={j > 0 ? 'text-center' : ''}>{c.trim().replace(/\*\*/g, '')}</span>)}</div>; }
                if (line.startsWith('**') && line.endsWith('**')) return <p key={i} className="text-sm font-semibold mt-4 mb-1">{line.replace(/\*\*/g, '')}</p>;
                if (line.trim() === '') return <div key={i} className="h-2" />;
                return <p key={i} className="text-sm text-foreground/90 mb-1">{line.replace(/\*\*(.+?)\*\*/g, '$1')}</p>;
              })}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  // Glossary term detail
  if (selectedTerm) {
    const color = GLOS_COLORS[selectedTerm.category] || '#5E656F';
    return (
      <div className="h-full overflow-y-auto px-6 lg:px-8 py-6 max-w-[860px]" data-testid="help-term-view">
        <Button variant="ghost" size="sm" className="mb-4" onClick={() => setSelectedTerm(null)}><ArrowLeft className="h-4 w-4 mr-1.5" /> Volver</Button>
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center gap-3 mb-4">
            <h1 className="font-display text-2xl font-semibold tracking-tight">{selectedTerm.term}</h1>
            <Badge style={{ background: `${color}15`, color, border: `1px solid ${color}30` }} className="text-[10px]">{glossaryCats.find(c => c.id === selectedTerm.category)?.label}</Badge>
          </div>
          <Card className="rounded-xl border-border/50 shadow-sm">
            <CardContent className="p-8 space-y-6">
              <div>
                <h3 className="text-xs font-display font-semibold uppercase tracking-wide text-muted-foreground mb-2" style={{ letterSpacing: '0.8px' }}>Definición simple</h3>
                <p className="text-base" style={{ lineHeight: 1.7 }}>{selectedTerm.simple}</p>
              </div>
              <Separator />
              <div>
                <h3 className="text-xs font-display font-semibold uppercase tracking-wide text-muted-foreground mb-2" style={{ letterSpacing: '0.8px' }}>Explicación técnica</h3>
                <p className="text-sm text-foreground/80" style={{ lineHeight: 1.7 }}>{selectedTerm.technical}</p>
              </div>
              <div className="p-4 rounded-xl border border-border/50" style={{ background: `${color}06` }}>
                <h3 className="text-xs font-display font-semibold uppercase tracking-wide mb-2" style={{ color, letterSpacing: '0.8px' }}>Ejemplo en desarrollo inmobiliario</h3>
                <p className="text-sm" style={{ lineHeight: 1.7 }}>{selectedTerm.example}</p>
              </div>
              <div className="p-4 rounded-xl bg-muted/30 border border-border/50">
                <h3 className="text-xs font-display font-semibold uppercase tracking-wide text-muted-foreground mb-2" style={{ letterSpacing: '0.8px' }}>¿Por qué es importante?</h3>
                <p className="text-sm" style={{ lineHeight: 1.7 }}>{selectedTerm.importance}</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto" data-testid="help-page">
      <div className="px-6 lg:px-8 py-6 max-w-[1200px]">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
          <div className="mb-6">
            <h1 className="font-display text-2xl font-semibold tracking-tight">Centro de Ayuda</h1>
            <p className="text-sm text-muted-foreground mt-1">Aprende a usar la plataforma y entiende los conceptos financieros</p>
          </div>

          <div className="relative mb-6 max-w-lg">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Buscar artículos o términos financieros..." className="pl-10 h-10" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>

          <Tabs defaultValue="articles">
            <TabsList className="mb-5">
              <TabsTrigger value="articles" className="gap-1.5"><BookOpen className="h-3.5 w-3.5" /> Guías ({filteredArticles.length})</TabsTrigger>
              <TabsTrigger value="glossary" className="gap-1.5"><GraduationCap className="h-3.5 w-3.5" /> Glosario ({filteredGlossary.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="articles" className="space-y-6">
              {categories.map(cat => {
                const catArticles = filteredArticles.filter(a => a.category === cat.id);
                if (catArticles.length === 0) return null;
                const Icon = CAT_ICONS[cat.id] || BookOpen;
                return (
                  <div key={cat.id}>
                    <h2 className="font-display text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2" style={{ letterSpacing: '0.8px' }}>
                      <Icon className="h-4 w-4" style={{ color: 'var(--primary-hex)' }} /> {cat.label}
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {catArticles.map(article => (
                        <Card key={article.id} className="rounded-xl border-border/50 shadow-sm hover:shadow-md transition-[box-shadow] duration-200 cursor-pointer group"
                          onClick={() => setSelectedArticle(article)} data-testid={`article-${article.id}`}>
                          <CardContent className="p-4">
                            <h3 className="text-sm font-display font-semibold group-hover:text-[color:var(--primary-hex)] transition-[color] duration-150">{article.title}</h3>
                            <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{article.summary}</p>
                            <span className="text-xs mt-2 inline-flex items-center gap-1" style={{ color: 'var(--primary-hex)' }}>Leer más <ChevronRight className="h-3 w-3" /></span>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })}
            </TabsContent>

            <TabsContent value="glossary">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {filteredGlossary.map(term => {
                  const color = GLOS_COLORS[term.category] || '#5E656F';
                  return (
                    <Card key={term.id} className="rounded-xl border-border/50 shadow-sm hover:shadow-md transition-[box-shadow] duration-200 cursor-pointer group"
                      onClick={() => setSelectedTerm(term)} data-testid={`term-${term.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <h3 className="text-sm font-display font-semibold group-hover:text-[color:var(--primary-hex)] transition-[color] duration-150">{term.term}</h3>
                            <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">{term.simple}</p>
                          </div>
                          <Badge style={{ background: `${color}12`, color, border: `1px solid ${color}25` }} className="text-[9px] shrink-0">{glossaryCats.find(c => c.id === term.category)?.label}</Badge>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>
    </div>
  );
}
